import { cn } from '@woonstad/helpers-utils';
import React, { forwardRef, ReactElement, isValidElement } from 'react';

import type { SVGComponent } from '../../types/SVGComponent';
import { Spinner } from '../spinner/Spinner';
import { Typography } from '../typography/Typography';

type Variant = 'primary' | 'login' | 'positive' | 'negative' | 'warning';
type Size = 'extra-small' | 'default' | 'large';

type Props = {
  children: ReactElement<any>;
  icon: SVGComponent;
  isLoading?: boolean;
  variant?: Variant;
  size?: Size;
  iconOnly?: boolean;
  isFullWidth?: boolean;
  reverse?: boolean;
  /**
   * Use only in exceptional cases, for example when you want to override the disabled state of a button.
   * Do not use this flag when you want to disable a button, use the disabled prop instead.
   *
   * It's only meant for exteptions where we want to have the styling of a button without actually rendering a button or link.
   */
  overrideDisabledState?: boolean;
  testid?: string;
};

type State = 'enabled' | 'disabled';

/* Tailwind purges dynamically constructed class names, so all classes must appear as full literals. */
const VARIANT_CLASSES: Record<Variant, Record<State, string>> = {
  primary:  { enabled: 'bg-button-primary-enabled text-button-primary-enabled',   disabled: 'bg-button-primary-disabled text-button-primary-disabled' },
  login:    { enabled: 'bg-button-login-enabled text-button-login-enabled',       disabled: 'bg-button-login-disabled text-button-login-disabled' },
  positive: { enabled: 'bg-button-positive-enabled text-button-positive-enabled', disabled: 'bg-button-positive-disabled text-button-positive-disabled' },
  negative: { enabled: 'bg-button-negative-enabled text-button-negative-enabled', disabled: 'bg-button-negative-disabled text-button-negative-disabled' },
  warning:  { enabled: 'bg-button-warning-enabled text-button-warning-enabled',   disabled: 'bg-button-warning-disabled text-button-warning-disabled' },
};

const VARIANT_ICON_BG: Record<Variant, Record<State, string>> = {
  primary:  { enabled: 'bg-button-icon-primary-enabled',  disabled: 'bg-button-icon-primary-disabled' },
  login:    { enabled: 'bg-button-icon-login-enabled',    disabled: 'bg-button-icon-login-disabled' },
  positive: { enabled: 'bg-button-icon-positive-enabled', disabled: 'bg-button-icon-positive-disabled' },
  negative: { enabled: 'bg-button-icon-negative-enabled', disabled: 'bg-button-icon-negative-disabled' },
  warning:  { enabled: 'bg-button-icon-warning-enabled',  disabled: 'bg-button-icon-warning-disabled' },
};

const VARIANT_ICON_TEXT: Record<Variant, Record<State, string>> = {
  primary:  { enabled: 'text-button-icon-primary-enabled',  disabled: 'text-button-icon-primary-disabled' },
  login:    { enabled: 'text-button-icon-login-enabled',    disabled: 'text-button-icon-login-disabled' },
  positive: { enabled: 'text-button-icon-positive-enabled', disabled: 'text-button-icon-positive-disabled' },
  negative: { enabled: 'text-button-icon-negative-enabled', disabled: 'text-button-icon-negative-disabled' },
  warning:  { enabled: 'text-button-icon-warning-enabled',  disabled: 'text-button-icon-warning-disabled' },
};

const SIZE_PADDING: Record<Size, { normal: string; reverse: string; iconOnly: string }> = {
  'extra-small': { normal: 'py-1 pl-3 pr-1 text-[13px]', reverse: 'py-1 pl-1 pr-3 text-[13px]', iconOnly: 'p-1' },
  'default':     { normal: 'py-1 pl-4 pr-1',             reverse: 'py-1 pl-1 pr-4',             iconOnly: 'p-1' },
  'large':       { normal: 'py-3 pl-6 pr-3',             reverse: 'py-3 pl-3 pr-6',             iconOnly: 'p-4' },
};

function computeDisabledState(children: ReactElement<any>, overrideDisabledState?: boolean): boolean {
  if (overrideDisabledState !== undefined) return overrideDisabledState;
  if (children.type === 'button') return (children.props as any).disabled;
  return !(children.props as any).href;
}

function getSizePadding(size: Size, iconOnly: boolean, reverse: boolean): string {
  const record = SIZE_PADDING[size];
  if (iconOnly) return record.iconOnly;
  if (reverse) return record.reverse;
  return record.normal;
}

function getButtonClasses(variant: Variant, size: Size, iconOnly: boolean, isFullWidth: boolean, reverse: boolean, hasDisableStyling: boolean) {
  const state: State = hasDisableStyling ? 'disabled' : 'enabled';
  const sizePadding = getSizePadding(size, iconOnly, reverse);

  const rootClasses = cn(
    'group inline-flex w-fit items-center rounded-full transition-shadow',
    sizePadding,
    VARIANT_CLASSES[variant][state],
    !hasDisableStyling && 'underline-offset-4 hover:underline hover:shadow-small',
    isFullWidth && 'w-full justify-between',
    reverse && 'flex-row-reverse',
  );

  const iconCircle = cn(
    'flex shrink-0 items-center overflow-hidden rounded-full',
    size === 'extra-small' ? 'size-6' : 'size-8',
    VARIANT_ICON_BG[variant][state],
    !hasDisableStyling && 'shadow-small',
  );

  const icon = cn(
    'pointer-events-none flex items-center justify-center transition-transform duration-200',
    size === 'extra-small' ? 'min-w-4 -translate-x-6' : 'min-w-5 -translate-x-8',
    !hasDisableStyling && 'group-hover:translate-x-0',
    VARIANT_ICON_TEXT[variant][state],
  );

  return { rootClasses, iconCircle, icon };
}

/**
 * Primaire button component.
 *
 * Visueel de meest prominente knop, wordt gebruikt voor de primary actie op een pagina.
 * Bijvoorbeeld submit knoppen of knoppen die de gebruiker naar een andere pagina brengt.
 */
export const ButtonPrimary = forwardRef((
  {
    children,
    icon: Icon,
    isLoading,
    variant = 'primary',
    size = 'default',
    iconOnly = false,
    isFullWidth = false,
    reverse = false,
    overrideDisabledState,
    testid,
  }: Props,
  ref,
) => {
  const isDisabled = computeDisabledState(children, overrideDisabledState);
  const hasDisableStyling = isLoading || isDisabled;
  const text = isValidElement(children) ? (children.props as any).children : '';
  const { rootClasses, iconCircle, icon: iconCls } = getButtonClasses(variant, size, iconOnly, isFullWidth, reverse, hasDisableStyling);

  return (
    <children.type
      {...(children.props as any)}
      ref={ref}
      className={rootClasses}
      data-testid={testid}
    >
      {!iconOnly && (
        size === 'extra-small' ? (
          <span className={cn('mr-1.5', reverse && 'ml-1.5 mr-0')}>
            {text}
          </span>
        ) : (
          <Typography
            className={cn(
              size === 'default' && 'mr-2',
              size === 'large' && 'mr-3',
              size === 'default' && reverse && 'ml-2 mr-0',
              size === 'large' && reverse && 'ml-3 mr-0',
            )}
            type={'span'}
            variant="label-regular"
          >
            {text}
          </Typography>
        )
      )}

      {iconOnly && <span className="sr-only">{text}</span>}

      {isLoading ? (
        <span aria-hidden className={`${iconCircle} justify-center`}>
          <div className="size-5">
            <Spinner />
          </div>
        </span>
      ) : (
        <span aria-hidden className={iconCircle}>
          <Icon className={cn(
            iconCls,
            size === 'extra-small' ? 'ml-[0.3rem] mr-2 size-5' : 'ml-[0.4rem] mr-3',
          )} />
          <Icon className={cn(iconCls, size === 'extra-small' && 'size-5')} />
        </span>
      )}
    </children.type>
  );
});

ButtonPrimary.displayName = 'ButtonPrimary';
